import { ChevronDown, Search } from 'lucide-react'
import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

interface Option {
	id: string
	name: string
	description?: string
}

const RENDER_LIMIT = 200

interface CommitmentMatrixMultiSelectorProps {
	title: string
	options: Option[]
	selected: string[]
	onChange: (selected: string[]) => void
	searchPlaceholder?: string
	emptyMessage?: string
	defaultOpen?: boolean
	loading?: boolean
}

export function CommitmentMatrixMultiSelector({
	title,
	options,
	selected,
	onChange,
	searchPlaceholder = 'Buscar...',
	emptyMessage = 'Nenhum item encontrado',
	defaultOpen = false,
	loading = false,
}: CommitmentMatrixMultiSelectorProps) {
	const [open, setOpen] = useState(defaultOpen)
	const [search, setSearch] = useState('')

	const matched = search.trim()
		? options.filter((opt) =>
				`${opt.name} ${opt.description ?? ''}`
					.toLowerCase()
					.includes(search.trim().toLowerCase()),
			)
		: options

	const selectedMatched = matched.filter((opt) => selected.includes(opt.id))
	const selectedOutsideMatch = options.filter(
		(opt) => selected.includes(opt.id) && !matched.includes(opt),
	)
	const pinned = [...selectedOutsideMatch, ...selectedMatched]
	const rest = matched
		.filter((opt) => !selected.includes(opt.id))
		.slice(0, Math.max(0, RENDER_LIMIT - pinned.length))

	const filtered = [...pinned, ...rest]
	const hiddenCount = matched.length - selectedMatched.length - rest.length

	const handleToggle = (id: string) => {
		onChange(
			selected.includes(id)
				? selected.filter((s) => s !== id)
				: [...selected, id],
		)
	}

	return (
		<div className="rounded-lg border border-kaefer-light-silver/60 overflow-hidden">
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				className="flex w-full cursor-pointer items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors"
			>
				<div className="flex items-center gap-2">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{title}
					</span>
					{selected.length > 0 && (
						<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-kaefer-medium-grey/20 px-1.5 text-[11px] font-semibold tabular-nums text-kaefer-medium-grey">
							{selected.length}
						</span>
					)}
				</div>
				<ChevronDown
					className={cn(
						'size-4 text-kaefer-medium-grey transition-transform duration-200',
						open && 'rotate-180',
					)}
				/>
			</button>

			{open && (
				<div className="flex flex-col gap-3 p-3 border-t border-kaefer-light-silver/40">
					{loading ? (
						<div className="flex flex-col gap-2">
							<Skeleton className="h-8 w-full" />
							<Skeleton className="h-8 w-full" />
							<Skeleton className="h-8 w-full" />
						</div>
					) : options.length === 0 ? (
						<p className="text-sm text-muted-foreground text-center py-2">
							{emptyMessage}
						</p>
					) : (
						<>
							<div className="relative">
								<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-kaefer-medium-grey/60 pointer-events-none" />
								<Input
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									placeholder={searchPlaceholder}
									className="h-8 pl-8 text-sm"
								/>
							</div>

							<div className="flex flex-col divide-y divide-kaefer-light-silver/30 max-h-56 overflow-y-auto rounded-md border border-kaefer-light-silver/40">
								{filtered.length === 0 ? (
									<p className="py-3 text-center text-sm text-muted-foreground">
										{emptyMessage}
									</p>
								) : (
									filtered.map((opt) => (
										<label
											key={opt.id}
											className="flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm transition-colors hover:bg-muted/30"
										>
											<input
												type="checkbox"
												checked={selected.includes(opt.id)}
												onChange={() => handleToggle(opt.id)}
												className="size-3.5 accent-kaefer-medium-grey"
											/>
											<div className="flex flex-col min-w-0">
												<span className="truncate font-medium text-kaefer-dark-grey">
													{opt.name}
												</span>
												{opt.description && (
													<span className="text-[11px] text-muted-foreground">
														{opt.description}
													</span>
												)}
											</div>
										</label>
									))
								)}
							</div>

							{hiddenCount > 0 && (
								<p className="text-center text-[11px] text-muted-foreground">
									Mostrando {filtered.length} de {matched.length} — refine a
									busca para ver os demais.
								</p>
							)}
						</>
					)}
				</div>
			)}
		</div>
	)
}
